import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, CheckCircle, Clock, DollarSign, Tag, Gauge, Sparkles,
  FileText, MessageSquare, RotateCcw, Copy, Download, Zap,
  ChevronRight, Layers, Bot, Users, Target, AlertCircle, Plus, Send
} from "lucide-react";
import api from "../lib/api";
import { FlowGrid, TraceProgress } from "../components/FlowVisuals";
import { FileUploader, FileList } from "../components/FileUpload";

const STAGE_ICONS = { received: Zap, analyzing: Sparkles, structuring: Layers, matching: Users, execution_started: Target, draft_in_progress: FileText, review_in_progress: CheckCircle, delivery_ready: Download, completed: CheckCircle };
const MODE_META = { quick: { label: "Quick Mode", desc: "AI-first, fastest", color: "#005CEE" }, deep: { label: "Deep Mode", desc: "Structured, higher quality", color: "#8A4FFF" }, manual: { label: "Manual Mode", desc: "Human-led, AI-assisted", color: "#005CEE" }, multi_agent: { label: "Multi-Agent", desc: "Parallel processing", color: "#8A4FFF" } };

export default function PosterWorkspace() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [revisionText, setRevisionText] = useState("");
  const [showRevision, setShowRevision] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [taskFiles, setTaskFiles] = useState([]);
  const pollRef = useRef(null);

  const fetchTask = useCallback(async () => {
    try {
      const res = await api.get(`/api/workspace/tasks/${taskId}`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 404) navigate("/my-work");
    } finally { setLoading(false); }
  }, [taskId, navigate]);

  useEffect(() => {
    fetchTask();
    pollRef.current = setInterval(fetchTask, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchTask]);

  useEffect(() => {
    if (!taskId) return;
    api.get(`/api/files/list?task_id=${taskId}`).then(r => setTaskFiles(r.data)).catch(() => {});
  }, [taskId]);

  const doAction = async (url, body = {}) => {
    setActionLoading(url);
    try {
      await api.post(url, body);
      await fetchTask();
    } catch (e) { console.error(e); }
    finally { setActionLoading(""); }
  };

  if (loading) return (
    <div style={{ paddingTop: 120, textAlign: "center" }}>
      <div style={{ width: 48, height: 48, border: "3px solid rgba(59,130,246,0.1)", borderTop: "3px solid var(--accent)", borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} />
      <p style={{ marginTop: 16, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.88rem" }}>Loading workspace...</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!data) return null;

  const { task, stages, activities, deliveries, notes } = data;
  const currentStageIdx = stages.findIndex(s => s.status === "active");
  const latestDelivery = deliveries?.[0];
  const isCompleted = task.status === "completed";
  const isReviewStage = task.current_stage === "review_in_progress";
  const modeMeta = MODE_META[task.execution_mode] || MODE_META.quick;

  return (
    <div style={{ paddingTop: 60, minHeight: "100vh", position: "relative" }}>
      <FlowGrid opacity={0.015} />
      {/* Top bar */}
      <div style={{ position: "fixed", top: 60, left: 0, right: 0, zIndex: 50, background: "rgba(248,250,252,0.85)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(200,210,225,0.4)", padding: "8px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <button data-testid="ws-back" onClick={() => navigate("/my-work")} style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid var(--bg-neutral)", background: "white", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ArrowLeft size={14} color="var(--text-muted)" />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.88rem", color: "var(--text-main)", lineHeight: 1.2 }}>{task.original_query}</div>
          <div style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.62rem", color: "var(--text-muted)" }}>
            {task.category?.replace(/_/g, " ")} · {modeMeta.label}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 100, background: isCompleted ? "rgba(5,150,105,0.08)" : `${modeMeta.color}10`, border: `1px solid ${isCompleted ? "rgba(5,150,105,0.2)" : modeMeta.color + "20"}` }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: isCompleted ? "var(--success)" : modeMeta.color, animation: isCompleted ? "none" : "pulse 2s ease-in-out infinite" }} />
          <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.62rem", fontWeight: 700, color: isCompleted ? "var(--success)" : modeMeta.color, letterSpacing: "0.05em" }}>
            {isCompleted ? "COMPLETED" : task.current_stage?.replace(/_/g, " ").toUpperCase()}
          </span>
        </div>
      </div>

      {/* 3-column layout */}
      <div style={{ paddingTop: 52, display: "grid", gridTemplateColumns: "280px 1fr 260px", minHeight: "calc(100vh - 112px)", gap: 0 }}>

        {/* ═══ LEFT: Task Intelligence ═══ */}
        <div data-testid="ws-left-panel" style={{ borderRight: "1px solid rgba(200,210,225,0.35)", padding: "20px 16px", overflowY: "auto", maxHeight: "calc(100vh - 112px)" }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 10 }}>TASK INTELLIGENCE</div>
            <h2 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text-main)", lineHeight: 1.2, marginBottom: 8 }}>{task.interpreted_goal || task.original_query}</h2>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
              {(task.tags || []).slice(0, 5).map((tag, i) => (
                <span key={i} style={{ fontSize: "0.58rem", fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: "var(--accent-subtle)", color: "var(--accent-text)", border: "1px solid rgba(59,130,246,0.1)", fontFamily: "'Satoshi', sans-serif" }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Metrics */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
            {[
              { label: "Confidence", value: `${Math.round(task.confidence * 100)}%`, Icon: Gauge, color: "var(--accent)" },
              { label: "Budget", value: `$${task.budget_min?.toLocaleString()}-${task.budget_max?.toLocaleString()}`, Icon: DollarSign, color: "var(--success)" },
              { label: "Timeline", value: `${task.timeframe_min}-${task.timeframe_max}d`, Icon: Clock, color: "var(--violet)" },
              { label: "Priority", value: task.priority || "normal", Icon: Zap, color: "#0891B2" },
            ].map(({ label, value, Icon, color }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.5)", border: "1px solid rgba(200,210,225,0.35)", borderRadius: 10, padding: "8px 10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
                  <Icon size={10} color={color} strokeWidth={1.5} />
                  <span style={{ fontSize: "0.52rem", fontWeight: 600, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.06em" }}>{label}</span>
                </div>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-main)", fontFamily: "'Satoshi', sans-serif" }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Stage Pipeline */}
          <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 10 }}>EXECUTION STAGES</div>
          <TraceProgress stages={stages} />
        </div>

        {/* ═══ CENTER: Live Output Canvas ═══ */}
        <div data-testid="ws-center-panel" style={{ padding: "20px 28px", overflowY: "auto", maxHeight: "calc(100vh - 112px)" }}>
          {/* Delivery content */}
          {latestDelivery ? (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <FileText size={15} color="var(--accent)" strokeWidth={1.5} />
                <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--text-main)" }}>{latestDelivery.title}</h3>
                <span style={{ fontSize: "0.58rem", fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: latestDelivery.status === "approved" ? "rgba(5,150,105,0.08)" : "var(--accent-subtle)", color: latestDelivery.status === "approved" ? "var(--success)" : "var(--accent-text)", fontFamily: "'Satoshi', sans-serif", border: `1px solid ${latestDelivery.status === "approved" ? "rgba(5,150,105,0.2)" : "rgba(59,130,246,0.1)"}` }}>
                  v{latestDelivery.version} · {latestDelivery.status}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {(latestDelivery.content_sections || []).map((section, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    style={{ background: "rgba(255,255,255,0.6)", backdropFilter: "blur(12px)", border: "1px solid rgba(200,210,225,0.35)", borderRadius: 16, padding: "1.2rem 1.4rem", boxShadow: "0 2px 12px rgba(0,0,0,0.02)" }}>
                    <h4 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.88rem", color: "var(--text-main)", marginBottom: 8 }}>{section.heading}</h4>
                    <div style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                      {section.content?.split("**").map((part, j) => j % 2 === 1 ? <strong key={j} style={{ color: "var(--text-main)", fontWeight: 600 }}>{part}</strong> : part)}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            /* Waiting state */
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
              <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}
                style={{ width: 64, height: 64, borderRadius: 20, background: "linear-gradient(135deg, var(--accent-subtle), rgba(139,92,246,0.06))", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20, boxShadow: "0 8px 32px rgba(59,130,246,0.08)" }}>
                <Sparkles size={24} color="var(--accent)" strokeWidth={1.5} />
              </motion.div>
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: "1rem", color: "var(--text-main)", marginBottom: 6 }}>
                {task.current_stage === "posted" ? "Task posted" : task.current_stage === "parsed" ? "Analyzing requirements..." : task.current_stage === "open_to_claim" ? "Waiting for an executor" : task.current_stage === "accepted" ? "Executor matched" : task.current_stage === "execution_started" ? "Execution in progress..." : task.current_stage === "revision_requested" ? "Revision requested" : "Preparing output..."}
              </p>
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-muted)", maxWidth: 360, textAlign: "center" }}>
                {task.current_stage === "open_to_claim" ? "Your task is published. Qualified executors can accept it." : task.current_stage === "accepted" ? `${task.assigned_doer_name || "An executor"} has accepted your task.` : "Your task is being processed. Output will appear here as soon as it's ready."}
              </p>
            </div>
          )}

          {/* Activity Feed */}
          <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid rgba(200,210,225,0.35)" }}>
            <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 12 }}>ACTIVITY</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(activities || []).slice(0, 15).map((act, i) => (
                <motion.div key={act.id || i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                  style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", marginTop: 6, flexShrink: 0, background: act.event_type === "approval" ? "var(--success)" : act.event_type === "revision" ? "var(--pink)" : act.event_type === "delivery" ? "var(--accent)" : "var(--bg-neutral)" }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-secondary)", lineHeight: 1.4 }}>{act.message}</p>
                    <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.6rem", color: "var(--text-muted)", marginTop: 2 }}>{new Date(act.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* ═══ RIGHT: Actions ═══ */}
        <div data-testid="ws-right-panel" style={{ borderLeft: "1px solid rgba(200,210,225,0.35)", padding: "20px 16px", overflowY: "auto", maxHeight: "calc(100vh - 112px)" }}>
          <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 12 }}>ACTIONS</div>

          {/* Approve / Revision (only when review stage) */}
          {isReviewStage && !isCompleted && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              <button data-testid="approve-btn" onClick={() => doAction(`/api/workspace/tasks/${taskId}/approve`)}
                disabled={actionLoading === `/api/workspace/tasks/${taskId}/approve`}
                style={{ width: "100%", padding: "0.6rem", borderRadius: 12, background: "var(--success)", color: "white", border: "none", fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: actionLoading ? 0.6 : 1 }}>
                <CheckCircle size={14} /> Approve delivery
              </button>
              <button data-testid="revision-toggle" onClick={() => setShowRevision(!showRevision)}
                style={{ width: "100%", padding: "0.5rem", borderRadius: 10, background: "rgba(255,255,255,0.6)", border: "1px solid rgba(200,210,225,0.4)", fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: "var(--text-secondary)" }}>
                <RotateCcw size={12} /> Request revision
              </button>
              <AnimatePresence>
                {showRevision && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                    <textarea data-testid="revision-input" className="form-input" value={revisionText} onChange={e => setRevisionText(e.target.value)} placeholder="What needs to change?" rows={2} style={{ fontSize: "0.78rem", resize: "vertical", marginBottom: 6 }} />
                    <button data-testid="revision-submit" onClick={() => { doAction(`/api/workspace/tasks/${taskId}/revision`, { note: revisionText }); setRevisionText(""); setShowRevision(false); }}
                      className="btn-primary" style={{ width: "100%", fontSize: "0.75rem", padding: "0.45rem" }}>Send revision request</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {isCompleted && (
            <div style={{ padding: "12px 14px", borderRadius: 12, background: "rgba(5,150,105,0.06)", border: "1px solid rgba(5,150,105,0.15)", marginBottom: 20, textAlign: "center" }}>
              <CheckCircle size={20} color="var(--success)" style={{ margin: "0 auto 6px" }} />
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.85rem", color: "var(--success)" }}>Task completed</p>
            </div>
          )}

          {/* Files */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 8 }}>ATTACHMENTS</div>
            <FileUploader taskId={taskId} onUpload={(f) => setTaskFiles(prev => [f, ...prev])} />
            <div style={{ marginTop: 8 }}>
              <FileList files={taskFiles} onDelete={async (id) => { try { await api.delete(`/api/files/${id}`); setTaskFiles(prev => prev.filter(f => f.id !== id)); } catch {} }} />
            </div>
          </div>

          {/* Add Note */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 8 }}>ADD NOTE</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input data-testid="note-input" className="form-input" value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." style={{ flex: 1, fontSize: "0.75rem" }}
                onKeyDown={e => { if (e.key === "Enter" && noteText.trim()) { doAction(`/api/workspace/tasks/${taskId}/note`, { note: noteText }); setNoteText(""); } }} />
              <button onClick={() => { if (noteText.trim()) { doAction(`/api/workspace/tasks/${taskId}/note`, { note: noteText }); setNoteText(""); } }}
                style={{ width: 32, height: 32, borderRadius: 8, background: "var(--primary)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Send size={12} color="white" />
              </button>
            </div>
          </div>

          {/* Notes list */}
          {notes?.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              {notes.slice(0, 5).map((n, i) => (
                <div key={n.id || i} style={{ padding: "6px 0", borderBottom: "1px solid rgba(228,234,242,0.3)" }}>
                  <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-secondary)" }}>{n.note}</p>
                  <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.55rem", color: "var(--text-muted)", marginTop: 2 }}>{n.author_name} · {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              ))}
            </div>
          )}

          {/* Execution Mode */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: "0.55rem", fontWeight: 700, color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif", letterSpacing: "0.1em", marginBottom: 8 }}>EXECUTION MODE</div>
            {Object.entries(MODE_META).map(([key, m]) => (
              <button key={key} onClick={() => doAction(`/api/workspace/tasks/${taskId}/mode`, { mode: key })}
                style={{ width: "100%", padding: "6px 10px", borderRadius: 8, marginBottom: 4, background: task.execution_mode === key ? `${m.color}08` : "transparent", border: `1px solid ${task.execution_mode === key ? m.color + "20" : "transparent"}`, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all 200ms ease", textAlign: "left" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: task.execution_mode === key ? m.color : "var(--bg-neutral)" }} />
                <div>
                  <div style={{ fontSize: "0.7rem", fontWeight: task.execution_mode === key ? 700 : 500, color: task.execution_mode === key ? m.color : "var(--text-secondary)", fontFamily: "'Satoshi', sans-serif" }}>{m.label}</div>
                  <div style={{ fontSize: "0.55rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>{m.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}
